import type { Catalog } from '../types';

export const social = {
  'timeline.empty.title': { pt: 'Sua timeline está vazia', en: 'Your timeline is empty' },
  'timeline.empty.body': {
    pt: 'Adicione quem você joga com frequência. As voltas deles aparecem aqui, e as suas na timeline deles.',
    en: 'Add the people you play with. Their rounds show up here, and yours in theirs.',
  },
  'timeline.addFriends': { pt: 'Adicionar amigos', en: 'Add friends' },
  'timeline.recordRound': { pt: 'Registrar volta', en: 'Record round' },
  'timeline.loadMore': { pt: 'Carregar mais {count}', en: 'Load {count} more' },
  'timeline.showing': { pt: 'Mostrando {shown} de {total}', en: 'Showing {shown} of {total}' },

  /**
   * O substantivo sozinho: o link no cabeçalho da timeline e o título da tela de amigos.
   *
   * Separado do `sidebar.friends`, que tem o mesmo texto hoje mas rotula um número — se um dia
   * couber "Amigos em comum" ali, é aquele que muda, não este.
   */
  'friends.label': { pt: 'Amigos', en: 'Friends' },
  'friends.title': { pt: 'Meus amigos', en: 'My friends' },
  'friends.add': { pt: 'Adicionar amigo', en: 'Add friend' },
  'friends.empty': { pt: 'Você ainda não tem amigos aqui', en: 'You have no friends here yet' },
  'friends.sortAZ': { pt: 'A–Z', en: 'A–Z' },
  'friends.hcp': { pt: 'HCP', en: 'HCP' },
  'friends.noHandicap': { pt: 'Sem handicap ainda', en: 'No handicap yet' },
  'friends.handicap': { pt: 'Handicap {value}', en: 'Handicap {value}' },
  'friends.count': {
    pt: { one: '{count} amigo', other: '{count} amigos' },
    en: { one: '{count} friend', other: '{count} friends' },
  },
  'friends.requestCount': {
    pt: { one: '{count} pedido', other: '{count} pedidos' },
    en: { one: '{count} request', other: '{count} requests' },
  },
  'friends.accept': { pt: 'Aceitar', en: 'Accept' },
  'friends.decline': { pt: 'Recusar', en: 'Decline' },

  'friends.requests': {
    pt: { one: 'Pedido recebido', other: 'Pedidos recebidos' },
    en: { one: 'Request received', other: 'Requests received' },
  },

  /**
   * "14 voltas juntos". Zero nunca chega aqui: a tela mostra a data da amizade nesse caso, porque
   * "0 voltas juntos" leria como placar quando o fato é que a dupla é nova.
   */
  'friends.roundsTogether': {
    pt: { one: '{count} volta juntos', other: '{count} voltas juntos' },
    en: { one: '{count} round together', other: '{count} rounds together' },
  },

  /** A contagem genérica de voltas, usada em várias telas. */
  'rounds.count': {
    pt: { one: '{count} volta', other: '{count} voltas' },
    en: { one: '{count} round', other: '{count} rounds' },
  },

  'friends.since': { pt: 'Amigos desde {date}', en: 'Friends since {date}' },
  'friends.emptyBody': {
    pt: 'Adicione quem você joga com frequência. As voltas deles aparecem na sua timeline, e as suas na timeline deles.',
    en: 'Add the people you play with. Their rounds show up in your timeline, and yours in theirs.',
  },
  'friends.neverPlayedTogether': {
    pt: 'Ainda não jogaram juntos',
    en: 'You have not played together yet',
  },

  'sidebar.aria': { pt: 'Você e seus amigos', en: 'You and your friends' },
  'sidebar.friends': { pt: 'Amigos', en: 'Friends' },
  'sidebar.roundsTogether': { pt: 'Voltas juntos', en: 'Rounds together' },
  'sidebar.addFriend': { pt: 'Adicionar', en: 'Add' },
  /** Sem o "ainda" da lista de amigos: aqui o rótulo divide espaço com o número. */
  'sidebar.noHandicap': { pt: 'Sem handicap', en: 'No handicap' },
  'sidebar.handicapShort': { pt: 'hcp {value}', en: 'hcp {value}' },

  'friends.back': { pt: 'Voltar', en: 'Back' },
  'friends.done': { pt: 'Concluído', en: 'Done' },

  // ------------------------------------------------------------ busca de jogador

  'search.placeholder': { pt: 'Nome ou email', en: 'Name or email' },
  'search.aria': {
    pt: 'Buscar jogador por nome ou email',
    en: 'Search for a player by name or email',
  },
  'search.results': { pt: 'Resultados', en: 'Results' },
  'search.empty.title': { pt: 'Ninguém encontrado', en: 'Nobody found' },
  'search.empty.body': {
    pt: 'O nome de exibição precisa começar pelo que você digitou, e o email precisa ser exato.',
    en: 'The display name has to start with what you typed, and the email has to be exact.',
  },
  'search.sent': { pt: 'Pedido enviado para {name}', en: 'Request sent to {name}' },
  'search.sendFailed': {
    pt: 'Não foi possível enviar o pedido',
    en: 'Could not send the request',
  },
  'search.mutualFriends': {
    pt: { one: '{count} amigo em comum', other: '{count} amigos em comum' },
    en: { one: '{count} mutual friend', other: '{count} mutual friends' },
  },
  'search.alreadyFriend': { pt: 'Já é amigo', en: 'Already a friend' },
  'search.requestSent': { pt: 'Pedido enviado', en: 'Request sent' },
  'search.invitedYou': { pt: 'Convidou você', en: 'Invited you' },

  'friends.nowFriends': { pt: 'Agora vocês são amigos', en: 'You are friends now' },
  'friends.requestDeclined': { pt: 'Pedido recusado', en: 'Request declined' },
  'friends.respondFailed': {
    pt: 'Não foi possível responder ao pedido',
    en: 'Could not answer the request',
  },

  // ---------------------------------------------------------- cartão do feed

  'feed.playingNow': { pt: 'Jogando agora', en: 'Playing now' },
  'feed.liveWhere': {
    pt: '{course} · buraco {done} de {total}',
    en: '{course} · hole {done} of {total}',
  },
  'feed.toPar': { pt: 'para o par', en: 'to par' },
  // O convite do 2a mora no cartão ao vivo: quem foi incluído no flight vê a volta acontecendo
  // e entra dali. Não há notificação separada para ler e descartar.
  'feed.joinFlight': { pt: 'Entrar na volta', en: 'Join this round' },
  'feed.joinFlightHint': {
    pt: 'Você está neste flight',
    en: 'You are in this flight',
  },
  'feed.progressAria': { pt: 'Progresso da volta', en: 'Round progress' },
  'feed.loweredHandicap': { pt: 'baixou o handicap para', en: 'lowered their handicap to' },
  'feed.personalBest': { pt: 'o melhor índice até agora', en: 'their best index so far' },
  'feed.wasBefore': { pt: 'era {value}', en: 'was {value}' },
  'feed.meta': {
    pt: '{when} · {course} · Tee {tee}',
    en: '{when} · {course} · Tee {tee}',
  },
  'feed.niceRound': { pt: 'Bom jogo', en: 'Nice round' },
  'feed.comment': { pt: 'Comentar', en: 'Comment' },
  /** "Bom jogo · 3" — o separador é pontuação, e a contagem some quando é zero. */
  'feed.withCount': { pt: '{label} · {count}', en: '{label} · {count}' },
  'feed.deleteComment': {
    pt: 'Apagar comentário de {name}',
    en: 'Delete {name}’s comment',
  },
  'feed.writeComment': { pt: 'Escreva um comentário', en: 'Write a comment' },
  'feed.send': { pt: 'Enviar', en: 'Send' },
  'feed.reactFailed': { pt: 'Não foi possível reagir', en: 'Could not react' },
  'feed.commentFailed': { pt: 'Não foi possível comentar', en: 'Could not comment' },
  'feed.deleteFailed': { pt: 'Não foi possível apagar', en: 'Could not delete' },
} as const satisfies Catalog;
